import React from "react";
import FormLayout from '../FormLayout';
import TextField from './TextField';
import SectorsField from '../../components/SectorsField';
import store from '../../redux';

/** Class representing a Sectors selector field. */
class SectorsSelectorField extends TextField {

    constructor(properties) {
        super({
            ...(SectorsSelectorField.getDefaultOptions()),
            ...properties,
            name: 'sectors'
        });

        this.type = FormLayout.SECTORS;

        // sort choices by ascending position
        this.choices = (this.choices || []).sort((a, b) => a.position - b.position);
    }

    /**
     * Get this field default options
     */
    static getDefaultOptions() {
        const properties = super.getDefaultOptions();
        const existing = properties.content && typeof properties.content === 'object' ? properties.content : {};
        const defaults = Array.isArray(existing.defaults) ? existing.defaults : [];

        properties.name     = 'sectors';
        properties.choices  = [];
        properties.expanded = false;
        properties.required = true;
        properties.multiple = true;
        properties.content  = { ...existing, defaults };

        return properties;
    }

    /**
     * Get choices refs, if any
     *
     * Refs are used to propose linked resources as choices,
     * like the sectors or abilities for example
     */
    getRefs() {
        const sectors = store?.getState()?.sectors?.sectors || [];
        return sectors?.map(sector => ({
            id: sector.id,
            name: sector.name,
        }));
    }

    /**
     * Get choices refs source, if any
     *
     * Refs are used to propose linked resources as choices,
     * like the sectors or abilities for example
     */
    getRefsSource() {
        return 'sectors';
    }

    /**
     * Return the list of options this field handles
     */
    getHandledOptions() {
        return [
            FormLayout.OPTION_WIDTH,
            FormLayout.OPTION_LABEL,
            FormLayout.OPTION_DESCRIPTION,
            FormLayout.OPTION_CHOICES,
            FormLayout.OPTION_DEFAULTS,
        ];
    }

    /**
     * Resolve sectors list from form resources or Redux store.
     * @param {object} [resources]
     * @returns {object[]}
     */
    resolveSectors(resources) {
        let sectors = resources?.sectors;
        if (!sectors?.length) {
            sectors = (store?.getState()?.sectors?.sectors || []);
        }
        return sectors || [];
    }

    /**
     * Choice ref_ids currently allowed as available sectors.
     * @returns {string[]}
     */
    availableChoiceIds() {
        return (this.choices || []).map(choice => choice.ref_id).filter(Boolean);
    }

    /**
     * Initial redux-form value: pre-selected sector objects (subset of choices).
     * @param {object} [resources]
     * @returns {object[]|undefined}
     */
    getFormInitialValue(resources) {
        const raw = this.content?.defaults;
        if (!raw || !Array.isArray(raw) || raw.length === 0) {
            return undefined;
        }
        const allowed = new Set(this.availableChoiceIds());
        const defaultIds = raw.map(String).filter(id => id && allowed.has(id));
        if (!defaultIds.length) {
            return undefined;
        }
        const byId = new Map(this.resolveSectors(resources).map(sector => [sector.id, sector]));
        const selected = defaultIds
            .map(id => byId.get(id))
            .filter(Boolean);
        return selected.length ? selected : undefined;
    }

    /**
     * Return this field preview input
     *
     * @param {Object} props with at least the following properties:
     *                 - {Bool} inEdition
     *                 - {Bool} inPreview
     *                 - {String} lang
     *                 - {Object} setting
     *                 - {Any} value
     *                 - {Object} resources
     */
    getInput(props) {
        const sector_ids = this.availableChoiceIds();
        const sectors = this.resolveSectors(props.resources)
            .filter(sector => sector_ids.includes(sector.id));

        return (
            <SectorsField
                sectors={sectors}
                name={this.reduxFieldName(props)}
                preventSubmit
            />
        );
    }

    /**
     * Return the icon to use on the editor button
     */
    getIcon() {
        return 'location-arrow';
    }
}

export default SectorsSelectorField;
